import {
  AttendanceClassDateSelectionPayload,
  AttendanceCollected,
  AttendanceDateSelectionPayload,
  AttendanceMonthRange,
} from './attendanceWizardTypes'

function parseYmd(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function formatYmd(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function enumerateYmdRange(startYmd: string, endYmd: string): string[] {
  const out: string[] = []
  const start = parseYmd(startYmd)
  const end = parseYmd(endYmd)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return out
  const cursor = new Date(start)
  while (cursor <= end) {
    out.push(formatYmd(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return out
}

function dateInSelectedMonths(
  date: string,
  months: AttendanceMonthRange[],
  keySet: Set<string>
): boolean {
  for (const month of months) {
    if (keySet.has(month.monthKey) && date >= month.startDate && date <= month.endDate) {
      return true
    }
  }
  return false
}

export function expandStudentMonthKeysToDates(
  payload: AttendanceDateSelectionPayload,
  monthKeys: string[]
): string[] {
  const keySet = new Set(monthKeys)
  const dates = new Set<string>()
  const aggregateMonths = payload.aggregate?.monthRanges ?? []

  for (const entry of payload.aggregate?.allDates ?? []) {
    if (dateInSelectedMonths(entry.date, aggregateMonths, keySet)) {
      dates.add(entry.date)
    }
  }

  for (const student of payload.students) {
    const studentMonths = student.monthRanges ?? []
    for (const day of student.dates) {
      if (
        dateInSelectedMonths(day, studentMonths, keySet) ||
        dateInSelectedMonths(day, aggregateMonths, keySet)
      ) {
        dates.add(day)
      }
    }
  }

  if (dates.size === 0) {
    for (const month of aggregateMonths) {
      if (keySet.has(month.monthKey)) {
        for (const day of enumerateYmdRange(month.startDate, month.endDate)) {
          dates.add(day)
        }
      }
    }
  }

  return [...dates].sort()
}

export function expandClassMonthKeysToDates(
  payload: AttendanceClassDateSelectionPayload,
  monthKeys: string[]
): string[] {
  const keySet = new Set(monthKeys)
  const dates = new Set<string>()
  const months = payload.monthRanges ?? []

  for (const day of payload.dates) {
    if (dateInSelectedMonths(day, months, keySet)) {
      dates.add(day)
    }
  }

  if (dates.size === 0) {
    for (const month of months) {
      if (keySet.has(month.monthKey)) {
        for (const day of enumerateYmdRange(month.startDate, month.endDate)) {
          dates.add(day)
        }
      }
    }
  }

  return [...dates].sort()
}

/** Payload emitted when the user confirms date/month picks in the date_selection UI. */
export type AttendanceDateSlotConfirm = {
  dateFrom: string
  dateTo: string
  selectedDates?: string[]
  selectedMonthKeys?: string[]
  monthLabels?: string[]
}

export function hasDiscreteDateSlotSelection(collected: AttendanceCollected): boolean {
  return (
    (collected.selectedDates?.length ?? 0) > 0 || (collected.selectedMonthKeys?.length ?? 0) > 0
  )
}

export function boundingRangeFromDates(dates: string[]): {dateFrom: string; dateTo: string} | null {
  if (dates.length === 0) return null
  const sorted = [...dates].sort()
  return {dateFrom: sorted[0], dateTo: sorted[sorted.length - 1]}
}

export function boundingRangeFromMonths(
  months: AttendanceMonthRange[]
): {dateFrom: string; dateTo: string} | null {
  if (months.length === 0) return null
  const sorted = [...months].sort((a, b) => a.startDate.localeCompare(b.startDate))
  return {dateFrom: sorted[0].startDate, dateTo: sorted[sorted.length - 1].endDate}
}

export function collectedFromDateSlotConfirm(
  collected: AttendanceCollected,
  confirm: AttendanceDateSlotConfirm
): AttendanceCollected {
  const next: AttendanceCollected = {
    ...collected,
    startDate: confirm.dateFrom,
    endDate: confirm.dateTo,
    selectedDates: confirm.selectedDates?.length ? confirm.selectedDates : undefined,
    selectedMonthKeys: confirm.selectedMonthKeys?.length ? confirm.selectedMonthKeys : undefined,
  }
  if (!next.selectedDates) delete next.selectedDates
  if (!next.selectedMonthKeys) delete next.selectedMonthKeys
  return next
}

export function collectedForDateSlotRangeRetry(
  collected: AttendanceCollected
): AttendanceCollected {
  const next = {...collected}
  delete next.selectedDates
  delete next.selectedMonthKeys
  return next
}

export function hadDateSlotUserSelection(collected: AttendanceCollected): boolean {
  return (
    (collected.selectedDates?.length ?? 0) > 0 ||
    (collected.selectedMonthKeys?.length ?? 0) > 0 ||
    Boolean(collected.startDate && collected.endDate)
  )
}
